import { Button, Col, Grid, Row } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  HeatingControlRelay,
  HeatingSystem,
  HeatingZoneID,
  RoomDialWrf06,
  RoomSensorNovos3,
  RoomSensorRegin,
  RoomSensorWrf04,
  RoomSensorWrf06,
  RuleActionType,
  RuleCategory,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { MAX_ZONES_ALLOWED } from '../../../../shared/helpers/configurationOptionGenerator';
import { BackButton } from '../../components/buttons/back-button';
import { DeviceSummary } from '../../components/layout/deviceSummary/device-summary';
import { Spinner } from '../../components/spinner/spinner';
import { hasCompleteParams, useBeeConfig } from '../../hooks/beeConfig';
import { useZones } from '../../hooks/zones';
import { routePaths } from '../../routes';
import './add-boiler-heating-zones.scss';
import { getDeviceWithZoneDescription } from './utils';

export const AddBoilerHeatingZonesView: React.FunctionComponent = () => {
  const { pushDevicesToConfig, getDevicesByType, getAssignedResourcesFor, devices, loading } = useBeeConfig();
  const { boilerType } = useParams<{ boilerType: string }>();
  const { push } = useRoutePaths();
  const { getZoneDescription } = useZones();

  type Sensor = RoomSensorWrf06 | RoomSensorWrf04 | RoomSensorNovos3 | RoomSensorRegin;

  const heatingZoneDevicesSummaryInfo: [string, { [key: string]: string }][] = React.useMemo(() => {
    const heatignZoneDevices = getDevicesByType<HeatingControlRelay | RoomDialWrf06 | Sensor>([
      DeviceType.RELAY,
      DeviceType.WRF04,
      DeviceType.WRF06,
      DeviceType.NOVOS3,
      DeviceType.REGIN,
    ]);
    return Object.entries(
      heatignZoneDevices.reduce((zones, device) => {
        const {
          deviceType,
          params: { zone_id: zoneID, action_type: actionType, address },
        } = device;

        const isPrimarySensor =
          actionType === RuleActionType.MONITORING && +(device as Sensor).params.primary_zone_temperature_sensor === 1;

        const deviceTypeAndActionType = `${deviceType}_${actionType}`;

        if (!zoneID) return zones;

        if (deviceTypeAndActionType === `${DeviceType.WRF06}_${RuleActionType.MONITORING}` && !isPrimarySensor)
          return zones;

        const primarySensor = isPrimarySensor ? deviceTypeAndActionType : zones[zoneID]?.primarySensor;

        const deviceDescription =
          deviceType === DeviceType.RELAY
            ? `Relay ${address}`
            : getDeviceWithZoneDescription(device as RoomDialWrf06 | Sensor, getZoneDescription);

        return {
          ...zones,
          [zoneID]: { ...zones[zoneID], [deviceTypeAndActionType]: deviceDescription, primarySensor },
        };
      }, {})
    );
  }, [devices]);

  const handleAddHeatingZone = () => {
    push(routePaths.heatingSystem.assignBoilerHeatingZones.configure, { boilerType });
  };

  const handleEditHeatingSystem = async (currentHeatingSystem, newHeatingSystem) => {
    const heatingZoneRelays = getDevicesByType<HeatingControlRelay>([DeviceType.RELAY]).filter(
      ({ params }) => params.zone_id && params.category === RuleCategory.HEATING
    );

    const devicesWithoutPreviousRelays = devices
      .filter(hasCompleteParams)
      .filter((device: HeatingControlRelay) => device.params.heating_system !== currentHeatingSystem);

    const relaysWithNewHeatingSystem = heatingZoneRelays.map<HeatingControlRelay>(relay => ({
      ...relay,
      params: { ...relay.params, heating_system: newHeatingSystem },
    }));

    await pushDevicesToConfig([...devicesWithoutPreviousRelays, ...relaysWithNewHeatingSystem]);
  };

  const handleNext = async () => {
    const currentlyConfiguredHeatingSystem = getAssignedResourcesFor(
      [DeviceType.RELAY, DeviceType.MASTERTHERM],
      'heating_system',
      null
    )[0];

    const newlySelectedHeatingSystem = HeatingSystem[boilerType.toUpperCase()];

    const hasHeatingSystemChanged = newlySelectedHeatingSystem !== currentlyConfiguredHeatingSystem;

    if (currentlyConfiguredHeatingSystem && hasHeatingSystemChanged) {
      handleEditHeatingSystem(currentlyConfiguredHeatingSystem, newlySelectedHeatingSystem);
    } else {
      await pushDevicesToConfig();
    }

    push(routePaths.hotWater.root);
  };

  const totalHeatingZones = heatingZoneDevicesSummaryInfo.length;

  const isSingleZone = !!heatingZoneDevicesSummaryInfo.find(([zoneID]) => zoneID === HeatingZoneID.WHOLE_HOME);

  return (
    <div>
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Boiler Heating Zones</span>
      </h1>
      <Grid className="grid">
        <Row>
          {heatingZoneDevicesSummaryInfo.map(([zoneID, summaryInfo]) => {
            const zonePrimarySensor = summaryInfo.primarySensor;

            return (
              <DeviceSummary
                key={zoneID}
                name={getZoneDescription(zoneID)}
                fields={[
                  { name: 'Temp Dial:', value: summaryInfo[`${DeviceType.WRF06}_${RuleActionType.CONTROL}`] },
                  {
                    name: 'Temp Sensor:',
                    value: summaryInfo[zonePrimarySensor],
                  },
                  { name: 'Control:', value: summaryInfo[`${DeviceType.RELAY}_${RuleActionType.CONTROL}`] },
                ]}
                data-qa={`heating-zone-summary-${zoneID}`}
                onSelect={() => {
                  push(routePaths.heatingSystem.assignBoilerHeatingZones.edit, { heatingZone: zoneID, boilerType });
                }}
              />
            );
          })}
        </Row>
      </Grid>
      <Row className="btn-row">
        <Col>
          <Button
            className="btn-full"
            onClick={handleAddHeatingZone}
            data-qa="add-button-heating-zone"
            disabled={isSingleZone || totalHeatingZones === MAX_ZONES_ALLOWED}
          >
            Add a heating zone
          </Button>
        </Col>
      </Row>
      <Row className="btn-row">
        <Button className="btn-full" onClick={handleNext} data-qa="continue-button" disabled={totalHeatingZones === 0}>
          Next
        </Button>
      </Row>
    </div>
  );
};
